/**
 * scheduleGanttPlotBands.ts
 * Builds Highcharts xAxis plotBands that shade non-work days (weekends + stat
 * holidays) on the Gantt timeline, so a multi-day bar visually reads as
 * spanning days the lab is actually closed.
 *
 * Kept out of ScheduleGantt.tsx so the chart-config file stays under the
 * 600-line cap and the band math is unit-checkable in isolation.
 *
 * Bands are reconciled by stable `id` in useGanttImperativeSync (add/remove
 * PlotBand) — NEVER via xAxis.update() — so they never re-init the treegrid
 * axis (BUG-2026-0093). Each weekend run and each holiday gets a deterministic
 * id derived from its date, so the same window produces the same ids.
 *
 * Color is a theme token passed in (no hex literals here): the caller passes
 * theme.schedule.* tokens for weekend vs holiday so the shading matches the
 * grid views.
 *
 * Run Mode Phase C: these bands deliberately KEEP rendering under continuous
 * (24/7) bookings — they describe the LAB (closed for people), not the
 * machine. Continuous bars sit above them at full strength (bands are
 * zIndex 0, series bars higher) with their own fill + tooltip, so a weekend
 * under a continuous bar reads "running through a closed day". No band logic
 * changes per mode, and the id-reconciliation contract (BUG-2026-0093 —
 * add/removePlotBand by stable id, never xAxis.update) is untouched.
 */

import type Highcharts from 'highcharts/highstock';
import { isWeekend, isStatHoliday } from '../../pages/schedule/views/scheduleGridShared.js';

/** One day in ms — band end is exclusive (start of next day). */
const MS_PER_DAY = 24 * 3600 * 1000;

/** 'YYYY-MM-DD' → Unix ms (UTC midnight). Mirrors ScheduleGantt.dateToMs. */
function isoToMs(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Ms (UTC midnight) → 'YYYY-MM-DD'. */
function msToIso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export interface NonWorkBandColors {
  /** Weekend (Sat/Sun) shading — subtle neutral tint. */
  weekend: string;
  /** Stat-holiday shading — distinct tint (lab closed). */
  holiday: string;
}

/**
 * Build weekend + holiday plotBands covering [startISO, endISO] inclusive.
 *
 * @param startISO  Window start 'YYYY-MM-DD'.
 * @param endISO    Window end 'YYYY-MM-DD' (inclusive).
 * @param holidays  Stat holidays from GET /api/schedule/working-days.
 * @param colors    Weekend / holiday theme tokens.
 */
export function buildNonWorkPlotBands(
  startISO: string,
  endISO: string,
  holidays: { date: string }[],
  colors: NonWorkBandColors,
): Highcharts.AxisPlotBandsOptions[] {
  if (!startISO || !endISO) return [];
  const startMs = isoToMs(startISO);
  const endMs = isoToMs(endISO);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return [];

  const bands: Highcharts.AxisPlotBandsOptions[] = [];
  // Low zIndex so the band sits BEHIND the bars / grid lines (zIndex 0).
  const baseZ = 0;

  // Walk every day in the window; emit one band per non-work day. Adjacent
  // weekend days (Sat+Sun) naturally render as a single continuous block since
  // each band's end is the start of the next day.
  for (let ms = startMs; ms <= endMs; ms += MS_PER_DAY) {
    const iso = msToIso(ms);
    const dayStart = ms;
    const dayEnd = ms + MS_PER_DAY;

    if (isStatHoliday(iso, holidays)) {
      // Holiday wins over weekend (a holiday on a weekend reads as a holiday).
      bands.push({
        id: `gantt-holiday-${iso}`,
        from: dayStart,
        to: dayEnd,
        color: colors.holiday,
        zIndex: baseZ,
      });
    } else if (isWeekend(iso)) {
      bands.push({
        id: `gantt-weekend-${iso}`,
        from: dayStart,
        to: dayEnd,
        color: colors.weekend,
        zIndex: baseZ,
      });
    }
  }

  return bands;
}
